import { styled } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { Tag } from 'antd';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import InnerHeader from '../../../component/InnerHeader';
import NoxTable from '../../../component/Table';
import { useResources, useDeleteResource } from '../../../hooks/resources';
import useToggle from '../../../hooks/useToggle';
import ResourceForm from '../../../component/forms/ResourceForm';

const Wrapper = styled('div')({
  height: 'calc(100vh-10.5rem)'
});

const transformData = (resources) => {
  if (!resources || !resources.length) {
    return [];
  }

  return resources.map(({ _id, name, type, monthlyCost }, idx) => {
    return {
      _id,
      key: idx,
      monthlyCost,
      name,
      type
    };
  });
};

const Resources = () => {
  const history = useHistory();
  const { handleOpen, handleClose, open } = useToggle();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { mutate: deleteResourceMutation, isLoading: deletingResorce } = useDeleteResource();
  const currentProject = useSelector(({ project }) => project.currentProject);
  const { data, isLoading: resourcesLoading, isFetching } = useResources(currentProject._id);

  const onViewClick = useCallback(
    (item) => {
      history.push(`/resources/${item._id}`, {
        projectId: currentProject._id,
        resourceId: item._id
      });
    },
    [currentProject._id, history]
  );

  const deleteResource = () => {
    selectedRowKeys.forEach((idx) => {
      const item = data.resources[idx];
      deleteResourceMutation({ projectId: currentProject._id, resourceId: item._id });
    });

    setSelectedRowKeys([]);
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: '_id',
        key: '_id',
        sorter: (a, b) => a._id - b._id,
        title: 'ID'
      },
      {
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.toString().localeCompare(b.name),
        title: 'Name'
      },
      {
        dataIndex: 'type',
        key: 'type',
        sorter: (a, b) => a.type.toString().localeCompare(b.type),
        title: 'Type'
      },
      {
        dataIndex: 'monthlyCost',
        key: 'monthlyCost',
        render: (cost) => <div>{cost}$</div>,
        sorter: (a, b) => a.monthlyCost - b.monthlyCost,
        title: 'Monthly Cost'
      },
      {
        key: 'view',
        render: (item) => <Tag onClick={() => onViewClick(item)}>View</Tag>
      }
    ],
    [onViewClick]
  );

  const actions = [
    {
      onClick: handleOpen,
      title: 'Create Resource',
      type: 'primary'
    }
  ];

  if (selectedRowKeys.length) {
    actions.unshift({
      onClick: deleteResource,
      title: 'Delete',
      type: 'default'
    });
  }

  const transformedData = useMemo(() => transformData(data?.resources), [data?.resources]);

  return (
    <Wrapper>
      <InnerHeader title="Resources" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={transformedData}
        loading={resourcesLoading || deletingResorce || isFetching}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <ResourceForm currentProject={currentProject._id} handleClose={handleClose} open={open} />
    </Wrapper>
  );
};

export default Resources;
